"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

export default function IndustriesAbilities({ data }) {
  const [activeTab, setActiveTab] = useState(data.tabs[0]?.id);
  
  const activeContent = data.tabs.find((tab) => tab.id === activeTab);

  if (!data || !activeContent) return null;

  return (
    <section 
      className="py-16 md:py-24 bg-gray-100" 
      aria-labelledby="industries-title"
    >
      <div className="max-w-7xl mx-auto px-6">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col items-center text-center gap-6 mb-16">
          <h2 
            id="industries-title"
            className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
          >
            {data.title}
          </h2>
          <div className="h-1.5 w-24 bg-brand rounded-full" aria-hidden="true" />
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed">
            {data.description}
          </p>
        </div>

        {/* --- TABS (Mobile Friendly & Accessible) --- */}
        <div 
          className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12" 
          role="tablist" 
          aria-label="Industry Categories"
        >
          {data.tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-bold text-sm uppercase tracking-widest transition-all duration-300 border-2 cursor-pointer
                ${activeTab === tab.id
                  ? "bg-brand border-brand text-white shadow-lg shadow-brand/20"
                  : "bg-white border-transparent text-slate-500 hover:border-slate-200"
                }`}
            >
              {tab.title}
            </button>
          ))}
        </div>

        {/* --- CONTENT AREA --- */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
            style={{ willChange: "opacity" }}
          >
            {/* Text Content */}
            <div className="flex flex-col gap-6">
              <h3 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight">
                {activeContent.heading}
              </h3>

              <ul className="space-y-4 marker:text-brand list-disc pl-6">
                {activeContent.points.map((item, index) => (
                  <li key={index} className="text-lg text-slate-700 leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Image Area */}
            <div className="relative aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-2xl bg-slate-200">
              <Image
                src={activeContent.image}
                alt={`Expertise in ${activeContent.heading}`}
                fill
                sizes="(max-width: 1024px) 100vw, 600px"
                className="object-cover"
                priority={true}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}